const FBC_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const PRIVACY_API_RETRY_INTERVAL_MS = 250;
const PRIVACY_API_RETRY_LIMIT = 40;

let initialized = false;
let privacyApiRetryCount = 0;
let privacyApiRetryTimeout = null;

function getCustomerPrivacy() {
    if (typeof window === "undefined") {
        return null;
    }

    return window.Shopify?.customerPrivacy || null;
}

export function isMarketingTrackingAllowed() {
    const customerPrivacy = getCustomerPrivacy();

    if (typeof customerPrivacy?.marketingAllowed !== "function") {
        return false;
    }

    try {
        return customerPrivacy.marketingAllowed() === true;
    } catch (error) {
        // sentry-ignore: marketing tracking must never block the customizer
        return false;
    }
}

export function getCookieValue(name) {
    if (!name || typeof document === "undefined") {
        return null;
    }

    const prefix = `${encodeURIComponent(name)}=`;
    const cookie = document.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix));

    if (!cookie) {
        return null;
    }

    const value = cookie.slice(prefix.length);

    try {
        return decodeURIComponent(value) || null;
    } catch (error) {
        // sentry-ignore: marketing tracking must never block the customizer
        return value || null;
    }
}

export function getFbclidFromUrl() {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        const url = new URL(window.location.href);
        const searchFbclid = url.searchParams.get("fbclid");

        if (searchFbclid) {
            return searchFbclid;
        }

        const hashQueryIndex = url.hash.indexOf("?");
        if (hashQueryIndex === -1) {
            return null;
        }

        return new URLSearchParams(url.hash.slice(hashQueryIndex + 1)).get("fbclid") || null;
    } catch (error) {
        // sentry-ignore: marketing tracking must never block the customizer
        return null;
    }
}

function setFbcCookie(value) {
    if (!value || typeof document === "undefined" || typeof window === "undefined") {
        return false;
    }

    try {
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        document.cookie = `_fbc=${encodeURIComponent(value)}; Path=/; Max-Age=${FBC_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
        return getCookieValue("_fbc") === value;
    } catch (error) {
        // sentry-ignore: marketing tracking must never block the customizer
        return false;
    }
}

export function ensureFbcCookie() {
    if (!isMarketingTrackingAllowed()) {
        return null;
    }

    const existingFbc = getCookieValue("_fbc");
    if (existingFbc) {
        return existingFbc;
    }

    const fbclid = getFbclidFromUrl();
    if (!fbclid) {
        return null;
    }

    const fbc = `fb.1.${Date.now()}.${fbclid}`;
    return setFbcCookie(fbc) ? fbc : null;
}

export function getMetaTracking() {
    if (!isMarketingTrackingAllowed()) {
        return null;
    }

    try {
        return {
            fbp: getCookieValue("_fbp"),
            fbc: ensureFbcCookie(),
            fbclid: getFbclidFromUrl(),
            clientUserAgent: typeof navigator !== "undefined" ? navigator.userAgent || null : null,
            eventSourceUrl: typeof window !== "undefined" ? window.location.href : null,
        };
    } catch (error) {
        // sentry-ignore: marketing tracking must never block the customizer
        return null;
    }
}

function schedulePrivacyApiRetry() {
    if (
        privacyApiRetryTimeout ||
        privacyApiRetryCount >= PRIVACY_API_RETRY_LIMIT ||
        typeof window === "undefined"
    ) {
        return;
    }

    privacyApiRetryCount += 1;
    privacyApiRetryTimeout = window.setTimeout(() => {
        privacyApiRetryTimeout = null;
        captureFbclidAfterConsent();
    }, PRIVACY_API_RETRY_INTERVAL_MS);
}

function captureFbclidAfterConsent() {
    const customerPrivacy = getCustomerPrivacy();

    if (typeof customerPrivacy?.marketingAllowed !== "function") {
        schedulePrivacyApiRetry();
        return;
    }

    ensureFbcCookie();
}

export function initializeMetaTracking() {
    if (initialized || typeof document === "undefined") {
        return;
    }

    initialized = true;
    document.addEventListener("visitorConsentCollected", captureFbclidAfterConsent);
    captureFbclidAfterConsent();
}
